import { useState } from "react";
import { api, ApiError } from "../api";

/**
 * NOVA CONTA BANCÁRIA — o pedido que vai para o DP aprovar.
 *
 * A conta só passa a valer depois da aprovação: é a conta onde cai o salário, e
 * trocá-la direto por uma sessão de celular é o golpe clássico de desvio de
 * pagamento. Por isso o formulário pede a SENHA de novo e a confirmação de que
 * a conta está no nome da própria pessoa.
 */

interface Props {
  tiposConta: string[];
  onEnviado: (mensagem: string) => void;
  onCancelar: () => void;
}

/** Os mais comuns aparecem como sugestão; qualquer outro pode ser digitado. */
const BANCOS = [
  "001 - Banco do Brasil",
  "033 - Santander",
  "077 - Inter",
  "104 - Caixa Econômica Federal",
  "237 - Bradesco",
  "260 - Nubank",
  "290 - PagBank",
  "323 - Mercado Pago",
  "336 - C6 Bank",
  "341 - Itaú",
  "380 - PicPay",
  "748 - Sicredi",
  "756 - Sicoob",
];

export default function ContaBancariaForm({ tiposConta, onEnviado, onCancelar }: Props) {
  const [banco, setBanco] = useState("");
  const [agencia, setAgencia] = useState("");
  const [conta, setConta] = useState("");
  const [tipo, setTipo] = useState(tiposConta[0] ?? "Corrente");
  const [pix, setPix] = useState("");
  const [titular, setTitular] = useState(false);
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await api.pedirConta({
        banco,
        agencia: agencia.trim(),
        conta: conta.trim(),
        tipo_conta: tipo,
        pix,
        titular,
        senha,
      });
      onEnviado(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível enviar o pedido.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="painel-form" onSubmit={enviar}>
      <p className="explica">
        A conta nova passa a valer <strong>depois que o DP aprovar</strong>. Até lá, o pagamento
        continua na conta atual.
      </p>

      <label className="campo">
        <span>Banco</span>
        <input list="lista-bancos" value={banco} onChange={(e) => setBanco(e.target.value)} placeholder="Ex.: 341 - Itaú" required />
        <datalist id="lista-bancos">
          {BANCOS.map((b) => (
            <option key={b} value={b} />
          ))}
        </datalist>
      </label>

      <div className="campos-lado">
        <label className="campo">
          <span>Agência</span>
          <input inputMode="numeric" value={agencia} onChange={(e) => setAgencia(e.target.value)} placeholder="1234" required />
        </label>
        <label className="campo">
          <span>Conta com dígito</span>
          <input inputMode="numeric" value={conta} onChange={(e) => setConta(e.target.value)} placeholder="123456-7" required />
        </label>
      </div>

      <label className="campo">
        <span>Tipo de conta</span>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {tiposConta.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span>Chave PIX (opcional)</span>
        <input value={pix} onChange={(e) => setPix(e.target.value)} placeholder="CPF, e-mail, telefone ou chave aleatória" />
      </label>

      <label className="caixa-marcar">
        <input type="checkbox" checked={titular} onChange={(e) => setTitular(e.target.checked)} />
        <span>Confirmo que esta conta está no meu nome e no meu CPF.</span>
      </label>

      <label className="campo">
        <span>Sua senha, para confirmar</span>
        <input type="password" autoComplete="current-password" value={senha} onChange={(e) => setSenha(e.target.value)} required />
      </label>

      {erro && <div className="aviso-erro">{erro}</div>}

      <div className="botoes">
        <button type="button" className="botao-texto" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </button>
        <button type="submit" className="botao-principal" disabled={enviando || !titular}>
          {enviando ? "Enviando…" : "Enviar para o DP"}
        </button>
      </div>
    </form>
  );
}
